/**
 * メディアの形式・寸法・日時に関する純粋な判定。
 * 方針は docs/decisions/0014-media-format-and-quality.md を参照。
 */

/** サムネイルの長辺(px) */
export const THUMBNAIL_MAX_EDGE = 512;
/** サムネイルの WebP 品質 */
export const THUMBNAIL_QUALITY = 0.75;
/** HEIC から変換するときの WebP 品質 */
export const WEBP_QUALITY = 0.9;
/** これを超える動画はアップロード前に警告する */
export const LARGE_VIDEO_BYTES = 300 * 1024 * 1024;

const HEIC_MIMES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);
const HEIC_EXTENSIONS = [".heic", ".heif"];

/**
 * HEIC かどうか。
 *
 * 拡張子も見るのは、ブラウザによっては HEIC の MIME を空文字で渡してくるため。
 */
export function isHeic(file: { type: string; name: string }): boolean {
  if (HEIC_MIMES.has(file.type.toLowerCase())) return true;
  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function isVideo(file: { type: string }): boolean {
  return file.type.startsWith("video/");
}

/**
 * 保存時の MIME。
 *
 * HEIC だけ WebP に変換する。JPEG や PNG は再エンコードすると劣化するので、
 * そのままの形式で保存する(→ ADR-0014)。
 */
export function storedMimeFor(file: { type: string; name: string }): string | null {
  if (isHeic(file)) return "image/webp";

  switch (file.type.toLowerCase()) {
    case "image/jpeg":
    case "image/png":
    case "image/webp":
    case "video/mp4":
    case "video/quicktime":
      return file.type.toLowerCase();
    default:
      return null;
  }
}

/**
 * 長辺を `maxEdge` に収める寸法。縦横比は保つ。
 * 元が小さい場合は拡大しない(引き伸ばすと粗くなるだけ)。
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) {
    throw new Error(`寸法が不正です: ${String(width)}x${String(height)}`);
  }
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };

  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * 撮影日時を決める。EXIF を優先し、無ければファイルの更新日時にする
 * (要件定義 3.2)。
 *
 * EXIF の日時はタイムゾーンを持たないため、撮影した端末のローカル時刻として
 * 解釈される。二人とも JST で使う前提なので、これで意図どおりになる。
 */
export function resolveTakenAt(exifDate: Date | undefined, fileLastModified: number): string {
  if (exifDate !== undefined && !Number.isNaN(exifDate.getTime())) {
    return exifDate.toISOString();
  }
  return new Date(fileLastModified).toISOString();
}

/** 動画の長さの表示 (`0:18` 形式) */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  return `${String(minutes)}:${String(total % 60).padStart(2, "0")}`;
}

/** バイト数を読める大きさにする。切りの良い1桁までで足りる */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : String(Math.round(value))} ${units[unit] ?? "TB"}`;
}
